import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import { api } from '../../lib/api';
import InvoiceLineItemsField, { lineItemsToWire } from './InvoiceLineItemsField';
import AccountPicker from './AccountPicker';
import DateInput from '../ui/DateInput';
import { WEBSITE_BUNDLE_LINES_DOLLARS } from '../../lib/website-bundle';

// NewInvoiceDialog — dual-mode form (create + edit).
//
// CREATE MODE (default — Sprint E item 4.2/4.3):
//   * "Save as Draft" — POSTs status='draft', stays in modal-close path.
//   * "Save & Send"   — POSTs status='sent' (server flips sent_at = now()),
//                       generates PDF + emits invoice_issued notification.
//   * Invoice number is auto-assigned by the server (not user-editable).
//   * Pre-fill modes (via `prefill`):
//       { account, project? }              — Per-account New Invoice button.
//       { account, lineItems, ... }        — Maintenance-renewal Draft Invoice.
//       { account, fromVoidedInvoice }     — Issue replacement after void.
//
// EDIT MODE (Sprint K-3):
//   * Caller sets mode="edit" and passes `editTarget` (full raw invoice row
//     plus its line_items array — fetched from /api/admin/financial/invoices/:id).
//   * All fields editable, INCLUDING invoice_number (only in edit mode —
//     create mode auto-assigns the next sequence number server-side).
//   * Single "Save changes" button.
//   * Submit fires two API calls:
//       1) PATCH /api/admin/financial/invoices/:id  — metadata
//       2) PATCH /api/admin/financial/invoices/:id/line-items — full sync
//          (existing items kept by id, new ones inserted, missing ones deleted)
//   * Server recomputes amount_cents from line items, so we don't send it.
//   * If the invoice is signed, edits create an invoice_revisions snapshot
//     server-side automatically (existing Sprint E.5 behaviour).

const DEFAULT_TERMS_DAYS = 30;

function todayIso() { return new Date().toISOString().slice(0, 10); }
function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Website-build bundle ($4,999) — mirrored from functions/_shared/website-bundle.js.
const DEFAULT_LINE_ITEMS = WEBSITE_BUNDLE_LINES_DOLLARS.map((l) => ({ ...l, qty: 1 }));

// Convert raw line_items row shape (snake_case, cents) to InvoiceLineItemsField
// display shape (camelCase, dollars). Preserves `id` so PATCH does updates
// instead of inserts.
function lineItemsFromRows(rows = []) {
  return rows.map((r) => ({
    id: r.id,
    description: r.description || '',
    qty: r.qty ?? 1,
    unitCostDollars: typeof r.unit_cost_cents === 'number' ? r.unit_cost_cents / 100 : 0,
    // MP.7 — keep the media-package product_type on edit round-trips so the
    // badge stays and re-saving doesn't strip it.
    product_type: r.product_type || null,
    product_metadata: (() => {
      if (!r.product_metadata_json) return null;
      try { return JSON.parse(r.product_metadata_json); } catch { return null; }
    })(),
  }));
}

export default function NewInvoiceDialog({
  open,
  onClose,
  prefill,
  defaultAccountLocked = false,
  onCreated,
  // Sprint K-3 — edit mode props.
  mode = 'create',
  editTarget = null, // { ...rawInvoiceRow, lineItems: [...rawRows] }
  onSaved, // edit-mode parallel to onCreated
}) {
  const isEdit = mode === 'edit';
  const editSignedAt = isEdit ? (editTarget?.signed_at || editTarget?.signedAt || null) : null;
  // Sprint K-2 — `accounts` state removed; AccountPicker fetches its own
  // search results. Project options stay because they're account-scoped.
  const [projects, setProjects] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState(''); // edit-only
  const [accountId, setAccountId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [validThrough, setValidThrough] = useState(addDays(todayIso(), DEFAULT_TERMS_DAYS));
  const [dueAt, setDueAt] = useState(''); // edit-only convenience; create infers from validThrough
  const [poNumber, setPoNumber] = useState('');
  const [items, setItems] = useState([...DEFAULT_LINE_ITEMS]);
  const [taxExempt, setTaxExempt] = useState(true);
  const [notesClient, setNotesClient] = useState('');
  const [notesInternal, setNotesInternal] = useState('');
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  // Sprint K-2 — account options pre-fetch removed; AccountPicker handles
  // its own server-side search + display. The defaultAccountLocked branch
  // renders a static label (see render block) without any fetch needed.

  // Project options scoped to the chosen account.
  useEffect(() => {
    if (!open || !accountId) { setProjects([]); return; }
    let alive = true;
    api.get(`/api/admin/accounts/${accountId}/projects`).then((d) => {
      if (alive) setProjects(d.projects || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [open, accountId]);

  // Apply prefill (create mode) or hydrate from editTarget (edit mode) on open.
  useEffect(() => {
    if (!open) return;
    setError(''); setWarning('');

    if (isEdit && editTarget) {
      setInvoiceNumber(editTarget.invoice_number || editTarget.invoiceNumber || '');
      setAccountId(editTarget.account_id || editTarget.accountId || '');
      setProjectId(editTarget.project_id || editTarget.projectId || '');
      setIssueDate((editTarget.issued_at || editTarget.issuedAt || '').slice(0, 10));
      setValidThrough((editTarget.valid_through || editTarget.validThrough || '').slice(0, 10));
      setDueAt((editTarget.due_at || editTarget.dueAt || '').slice(0, 10));
      setPoNumber(editTarget.po_number || editTarget.poNumber || '');
      const incomingItems = editTarget.lineItems || editTarget.line_items || [];
      setItems(incomingItems.length ? lineItemsFromRows(incomingItems) : [...DEFAULT_LINE_ITEMS]);
      setTaxExempt(
        editTarget.tax_exempt === 1 || editTarget.tax_exempt === true ||
        editTarget.taxExempt === 1 || editTarget.taxExempt === true,
      );
      setNotesClient(editTarget.notes_client || editTarget.notesClient || '');
      setNotesInternal(editTarget.notes_internal || editTarget.notesInternal || editTarget.notes || '');
      setSignatureRequired(
        editTarget.signature_required === 1 || editTarget.signature_required === true ||
        editTarget.signatureRequired === 1 || editTarget.signatureRequired === true,
      );
      return;
    }

    // Create-mode prefill.
    setInvoiceNumber('');
    setAccountId(prefill?.account?.id || '');
    setProjectId(prefill?.projectId || '');
    setIssueDate(prefill?.issueDate || todayIso());
    setValidThrough(prefill?.validThrough || addDays(todayIso(), DEFAULT_TERMS_DAYS));
    setDueAt('');
    setPoNumber(prefill?.poNumber || '');
    setItems(prefill?.lineItems && prefill.lineItems.length ? prefill.lineItems : [...DEFAULT_LINE_ITEMS]);
    setTaxExempt(prefill?.taxExempt ?? (prefill?.account?.type === 'school'));
    setNotesClient(prefill?.notesClient || '');
    setNotesInternal(prefill?.notesInternal || '');
    setSignatureRequired(!!prefill?.signatureRequired);
  }, [open, prefill, isEdit, editTarget]);

  const totalDollars = useMemo(
    () => items.reduce((s, r) => s + ((Number(r.qty) || 0) * (Number(r.unitCostDollars) || 0)), 0),
    [items],
  );

  const submit = async (saveMode) => {
    setError(''); setWarning('');
    if (!accountId) { setError('Account is required'); return; }
    if (!issueDate) { setError('Issue date is required'); return; }
    if (isEdit && !invoiceNumber.trim()) { setError('Invoice number is required'); return; }
    const wireItems = lineItemsToWire(items).filter((r) => r.description && r.qty > 0);
    if (wireItems.length === 0) { setError('At least one line item is required'); return; }
    setSubmitting(true);
    try {
      if (isEdit) {
        // Edit: PATCH metadata first, then PATCH line items as a full sync.
        // Server recomputes amount_cents from line items, so we don't send it.
        // signatureRequired is editable here too. Turning it off never drops
        // a signature that was already captured; turning it on for an unsigned
        // sent invoice starts asking the school to sign in the portal.
        const metadata = {
          accountId,
          projectId: projectId || null,
          invoiceNumber: invoiceNumber.trim(),
          issuedAt: issueDate,
          dueAt: dueAt || null,
          validThrough: validThrough || null,
          poNumber: poNumber.trim() || null,
          taxExempt: taxExempt ? 1 : 0,
          signatureRequired: signatureRequired ? 1 : 0,
          notesClient: notesClient.trim() || null,
          notesInternal: notesInternal.trim() || null,
        };
        await api.patch(`/api/admin/financial/invoices/${editTarget.id}`, metadata);

        // Preserve existing item ids so PATCH does updates rather than
        // recreating rows (which would orphan history). Items without an id
        // are inserts; ids on the row but not in payload are deletes.
        const itemsForSync = wireItems.map((r, idx) => {
          const original = items[idx];
          return original?.id && !String(original.id).startsWith('tmp-')
            ? { ...r, id: original.id }
            : r;
        });
        await api.patch(`/api/admin/financial/invoices/${editTarget.id}/line-items`, {
          items: itemsForSync,
        });

        onSaved?.();
        onClose?.();
        return;
      }

      // Create flow.
      const payload = {
        accountId,
        projectId: projectId || null,
        issuedAt: issueDate,
        validThrough,
        poNumber: poNumber.trim() || null,
        taxExempt: taxExempt ? 1 : 0,
        signatureRequired: signatureRequired ? 1 : 0,
        notesClient: notesClient.trim() || null,
        notesInternal: notesInternal.trim() || null,
        lineItems: wireItems,
        amountCents: Math.round(totalDollars * 100),
        currency: 'USD',
        status: saveMode === 'send' ? 'sent' : 'draft',
      };
      const res = await api.post('/api/admin/financial/invoices', payload);
      onCreated?.(res.invoice, { mode: saveMode });
      onClose?.();
    } catch (err) {
      const msg = err?.data?.error || err?.message || (isEdit ? 'Save failed' : 'Create failed');
      if (err?.data?.warning === 'out_of_sequence') {
        setWarning(msg);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // accountOptions removed in Sprint K-2 — AccountPicker fetches its own
  // search results, and the locked branch renders a static label.

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? 'Edit invoice' : 'New invoice'}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          {warning ? (
            <div className="text-[12px] text-amber-300/90 flex-1">{warning}. Saving anyway will keep the entered number.</div>
          ) : <span />}
          <div className="ml-auto flex items-center gap-3">
            <button type="button" onClick={onClose} disabled={submitting}
              className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
              Cancel
            </button>
            {isEdit ? (
              <button type="button" data-testid="edit-invoice-save" onClick={() => submit('save')} disabled={submitting}
                className="px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 shadow-lg shadow-[#B0FC38]/20">
                {submitting ? 'Saving…' : 'Save changes'}
              </button>
            ) : (
              <>
                <button type="button" data-testid="new-invoice-save-draft" onClick={() => submit('draft')} disabled={submitting}
                  className="px-4 py-2.5 rounded-full border border-white/[0.15] text-sm text-white hover:bg-white/[0.05]">
                  {submitting ? 'Saving…' : 'Save as Draft'}
                </button>
                <button type="button" data-testid="new-invoice-save-send" onClick={() => submit('send')} disabled={submitting}
                  className="px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 shadow-lg shadow-[#B0FC38]/20">
                  {submitting ? 'Sending…' : 'Save & Send'}
                </button>
              </>
            )}
          </div>
        </div>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); submit(isEdit ? 'save' : 'draft'); }} className="space-y-4">
        {isEdit && (editTarget?.signed_at || editTarget?.signedAt) && (
          <div className="px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200">
            This invoice is signed. Edits create a new revision but the signature stays intact.
            For substantive changes (amount/account), consider voiding + reissuing instead.
          </div>
        )}

        {isEdit && (
          <Field label="Invoice number *">
            <input
              type="text"
              data-testid="edit-invoice-number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className={inputCls}
              placeholder="e.g. INV-2026-0042"
            />
            <span className="block text-[11px] text-gray-500 mt-1">
              Manual numbering, make sure it doesn't collide with another invoice.
            </span>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Account *">
            {!isEdit && defaultAccountLocked && prefill?.account ? (
              // Prefill is locked (e.g., creating from an account page) — render
              // as a static label, not a picker, to make the lock obvious.
              <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white">
                {prefill.account.name}
              </div>
            ) : (
              <AccountPicker
                value={accountId}
                onChange={(id) => setAccountId(id)}
                required
                disabled={submitting}
              />
            )}
          </Field>
          <Field label="Project (optional)">
            <select
              data-testid="new-invoice-project"
              disabled={submitting || projects.length === 0}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className={selectCls}
            >
              <option value="">None</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Issue date *">
            <DateInput data-testid="new-invoice-issue-date" value={issueDate} onChange={setIssueDate} className={inputCls} />
          </Field>
          <Field label="Valid through">
            <DateInput data-testid="new-invoice-valid-through" value={validThrough} onChange={setValidThrough} className={inputCls} />
          </Field>
          {isEdit && (
            <Field label="Due date">
              <DateInput data-testid="edit-invoice-due-at" value={dueAt} onChange={setDueAt} className={inputCls} />
            </Field>
          )}
          <Field label="PO Number">
            <input type="text" data-testid="new-invoice-po-number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Tax">
            <label className="inline-flex items-center gap-2 text-[13px] text-gray-200 mt-2">
              <input
                type="checkbox"
                data-testid="new-invoice-tax-exempt"
                checked={taxExempt}
                onChange={(e) => setTaxExempt(e.target.checked)}
              />
              Tax-exempt (NYC DOE)
            </label>
          </Field>
        </div>

        <InvoiceLineItemsField items={items} onChange={setItems} disabled={submitting} />

        <Field label="Notes to client">
          <textarea data-testid="new-invoice-notes-client" rows={2} value={notesClient} onChange={(e) => setNotesClient(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Internal notes (admin/ops only)">
          <textarea data-testid="new-invoice-notes-internal" rows={2} value={notesInternal} onChange={(e) => setNotesInternal(e.target.value)} className={inputCls} />
        </Field>

        <label className="inline-flex items-start gap-2 text-[13px] text-gray-200">
          <input
            type="checkbox"
            data-testid="new-invoice-signature-required"
            checked={signatureRequired}
            onChange={(e) => setSignatureRequired(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Signature required
            {isEdit && editSignedAt && (
              <span className="block text-[11px] text-gray-500">
                Already signed {String(editSignedAt).slice(0, 10)} — the captured signature is kept either way.
              </span>
            )}
            {isEdit && !editSignedAt && (
              <span className="block text-[11px] text-gray-500">
                {signatureRequired ? 'The school will be asked to e-sign this invoice in their portal.' : 'No signature will be requested.'}
              </span>
            )}
          </span>
        </label>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white';
const selectCls = inputCls;

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
